"use client";

import { IconStar } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Rating } from "@/components/common/rating";
import { DietCardRow } from "@/features/cart/components/configure-diet/diet-card/diet-card-row";
import type { DietCatalog, DietCatalogDetail } from "@/features/diets/types";
import type { Globals } from "@/features/globals/types/globals.type";
import { RatingsDialog } from "@/features/rating/components/ratings-dialog";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";

/**
 * Whether the reviews row is rendered for the given diet. Exported so cards can
 * adjust their layout when the row is absent.
 */
export const hasDietReviewsRow = (
  diet: DietCatalog | DietCatalogDetail,
  globals?: Globals | null,
) =>
  !!globals?.ratingDietListing && !!diet.rating?.value && !!diet.rating?.count;

/**
 * Diet card row opening the reviews dialog. Rendered only when the diet has a
 * rating to show and ratings are enabled for the listing.
 */
export const DietCardReviewsRow = ({
  diet,
  globals,
  compact,
}: {
  diet: DietCatalog | DietCatalogDetail;
  globals?: Globals | null;
  compact?: boolean;
}) => {
  const t = useTranslations("diets.card");

  const rating = diet.rating;

  if (!(hasDietReviewsRow(diet, globals) && rating)) {
    return null;
  }

  const canOpenReviews = globals?.ratingDietDetails;

  const row = (
    <DietCardRow
      interactive={canOpenReviews}
      addon={
        <Rating
          count={rating.count}
          productId={diet.id}
          productName={formatDietName(diet.name)}
          rating={rating.value}
          size="md"
        />
      }
      icon={<IconStar className="size-5" stroke={1.5} />}
      label={compact ? t("reviewsShort") : t("reviews")}
    />
  );

  if (!canOpenReviews) {
    return row;
  }

  return (
    <RatingsDialog
      merchantName={formatCateringName(diet.catering.name)}
      productId={diet.id}
      productName={formatDietName(diet.name)}
      trigger={row}
      triggerAsChild
    />
  );
};
